import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import {
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const EnhancedCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
    borderColor: theme.palette.primary.main,
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const statusColors = {
    pending: { bg: theme.palette.warning.main, color: theme.palette.warning.contrastText },
    approved: { bg: theme.palette.info.main, color: theme.palette.info.contrastText },
    completed: { bg: theme.palette.success.main, color: theme.palette.success.contrastText },
    cancelled: { bg: theme.palette.error.main, color: theme.palette.error.contrastText },
    paid: { bg: theme.palette.success.main, color: theme.palette.success.contrastText },
    funded: { bg: theme.palette.success.dark, color: theme.palette.success.contrastText },
    confirmed: { bg: theme.palette.info.main, color: theme.palette.info.contrastText },
  };
  const colorConfig = statusColors[status] || {
    bg: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  };
  return {
    backgroundColor: colorConfig.bg,
    color: colorConfig.color,
    fontWeight: 600,
    fontSize: '0.7rem',
    textTransform: 'capitalize',
    borderRadius: 6,
    height: 20,
  };
});

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });

const InvoiceCard = ({ 
  invoice, 
  getDisplayStatus, 
  getDisplayStatusKey,
  getFinalizedAmount,
  theme 
}) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusKey = getDisplayStatusKey(invoice.status);
  
  // UPDATED: Fallback to created_at if payout_date is missing, then to current date
  const getDisplayDate = () => {
    if (invoice.payout_date) {
      try {
        return new Date(invoice.payout_date).toLocaleDateString();
      } catch (e) {
        console.warn('Invalid payout_date:', invoice.payout_date);
      }
    }
    
    if (invoice.created_at) {
      try {
        return new Date(invoice.created_at).toLocaleDateString();
      } catch (e) {
        console.warn('Invalid created_at:', invoice.created_at);
      }
    }
    
    // Last resort: show current date
    return new Date().toLocaleDateString();
  };

  const displayDate = getDisplayDate();

  // Extract BOTH company and person info
  const getSourceInfo = () => {
    let companyName = null;
    let personName = null;

    // Try to get company name
    if (invoice.company?.name) {
      companyName = invoice.company.name;
    } else if (invoice.client_name) {
      companyName = invoice.client_name;
    } else if (invoice.user?.company_name) {
      companyName = invoice.user.company_name;
    }

    // Try to get person name
    if (invoice.user?.username) {
      personName = invoice.user.username;
    } else if (invoice.user?.first_name && invoice.user?.last_name) {
      personName = `${invoice.user.first_name} ${invoice.user.last_name}`;
    } else if (invoice.client_email) {
      personName = invoice.client_email.split('@')[0];
    }

    // Format
    if (companyName && personName) {
      return { company: companyName, person: personName };
    } else if (companyName) {
      return { company: companyName, person: null };
    } else if (personName) {
      return { company: personName, person: null };
    }

    return { 
      company: invoice.po_number ? `PO: ${invoice.po_number}` : `Invoice #${invoice.id}`, 
      person: null 
    };
  };

  const sourceInfo = getSourceInfo();
  const poNumber = invoice.po_number || invoice.unique_code || `#${invoice.id}`;

  return (
    <EnhancedCard>
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header - Company and Person */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <ReceiptIcon color="secondary" sx={{ fontSize: 18 }} />
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {sourceInfo.company}
              </Typography>
            </Box>
            <StatusChip status={statusKey} label={getDisplayStatus(invoice.status)} size="small" />
          </Box>
          {sourceInfo.person && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {sourceInfo.person}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          PO: {poNumber}
        </Typography>

        {/* Amount */}
        <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
          {currency.format(getFinalizedAmount(invoice))}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Date:</strong> {displayDate}
        </Typography>

        {/* Notes - Collapsible */}
        {invoice.notes && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              <Typography variant="caption">
                {expanded ? 'Hide' : 'Show'} Details
              </Typography>
            </Button>
            <Collapse in={expanded}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                {invoice.notes}
              </Typography>
            </Collapse>
          </Box>
        )}

        {/* Client Notes - Collapsible */}
        {invoice.client_notes && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Client Notes:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
              {invoice.client_notes}
            </Typography>
          </Box>
        )}

        {/* Show BlitzPay indicator if applicable */}
        {invoice.blitzpay && (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label="BlitzPay" 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>
        )}

        {/* Show if payment method is pending */}
        {invoice.pending_payment_method && (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label="Payment Method Needed" 
              size="small" 
              color="warning" 
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, mt: 'auto' }}>
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          component={Link}
          to={`/invoicing/${invoice.id}`}
          startIcon={<ReceiptIcon />}
          sx={{ borderRadius: 2 }}
        >
          View Invoice
        </Button>
      </CardActions>
    </EnhancedCard>
  );
};

export default InvoiceCard;